/**
 * PartnerCommissions — iter98
 * Recurring revenue summary + full history table.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CalendarRange, Calendar, Wallet, CheckCircle2 } from 'lucide-react';
import PartnerAccountLayout from '../../../components/PartnerAccountLayout';
import { API, authHeaders, fmtMoney, fmtDate } from './_utils';

function Stat({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className={`rounded-2xl p-5 border ${accent ? 'bg-gradient-to-br from-amber-500/[0.08] to-gold-500/[0.04] border-gold-500/30' : 'bg-white/[0.025] border-white/[0.08]'}`} data-testid={testId}>
      <Icon className={`w-4 h-4 mb-2 ${accent ? 'text-gold-300' : 'text-white/55'}`} />
      <div className={`text-2xl font-black ${accent ? 'text-gold-200' : 'text-white'}`}>{value}</div>
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 mt-1.5 font-bold">{label}</p>
    </div>
  );
}

const STATUS_COLORS = {
  paid:      'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  succeeded: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending:   'bg-amber-500/15   text-amber-300   border-amber-500/30',
  failed:    'bg-red-500/15     text-red-300     border-red-500/30',
};

export default function PartnerCommissions() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [dash, list, refs] = await Promise.all([
          axios.get(`${API}/partners/dashboard`, { headers: authHeaders() }),
          axios.get(`${API}/partners/commissions`, { headers: authHeaders() }),
          axios.get(`${API}/partners/referrals`, { headers: authHeaders() }),
        ]);
        setOverview(dash.data.overview);
        setCommissions(list.data.items || []);
        setReferrals(refs.data.items || []);
      } catch {
        toast.error('Could not load commissions');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const restaurantNameById = (rid) => {
    const r = referrals.find((x) => (x.restaurant_id || x.id) === rid);
    return r?.restaurant_name || rid || '—';
  };

  if (loading) {
    return (
      <PartnerAccountLayout currentKey="commissions" title="Recurring Revenue">
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-gold-300" /></div>
      </PartnerAccountLayout>
    );
  }

  const totalEarned = overview?.commission_total_cents ?? 0;
  // Earned = same as paid for now (we only track paid commissions). Future:
  // separate "earned but pending payout" once batch payouts are introduced.
  const totalPaid = totalEarned;

  return (
    <PartnerAccountLayout
      currentKey="commissions"
      title="Recurring Revenue"
      subtitle="Track every recurring monthly payment generated by your member restaurants."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat icon={CalendarRange} label="This Month"     value={fmtMoney(overview?.commission_this_month_cents)} accent testId="partner-comm-month" />
        <Stat icon={Calendar}      label="Last Month"     value={fmtMoney(overview?.commission_last_month_cents)} testId="partner-comm-last-month" />
        <Stat icon={Wallet}        label="Total Earned"   value={fmtMoney(totalEarned)} testId="partner-comm-total-earned" />
        <Stat icon={CheckCircle2}  label="Total Paid"     value={fmtMoney(totalPaid)} testId="partner-comm-total-paid" />
      </div>

      <h2 className="text-lg font-bold text-white mb-4">Recurring Revenue History</h2>
      {commissions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/[0.08] bg-white/[0.015] p-10 text-center">
          <Wallet className="w-7 h-7 text-white/40 mx-auto mb-3" />
          <p className="text-sm font-bold text-white mb-1">No recurring revenue yet</p>
          <p className="text-xs text-white/50 max-w-[420px] mx-auto">
            Once your member restaurants start paying, every monthly payment lands here automatically.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-x-auto" data-testid="partner-commissions-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {['Restaurant', 'Month', 'Revenue Generated', 'Partner Revenue', 'Payment Status'].map((h) => (
                  <th key={h} className="text-left text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold px-5 py-3.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.id || `${c.restaurant_id}-${c.created_at}`} className="border-b border-white/[0.03] last:border-0">
                  <td className="px-5 py-3.5 text-white/90 font-semibold whitespace-nowrap">{restaurantNameById(c.restaurant_id)}</td>
                  <td className="px-5 py-3.5 text-white/75 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="px-5 py-3.5 text-white/85 whitespace-nowrap">{fmtMoney(c.revenue_cents)}</td>
                  <td className="px-5 py-3.5 text-gold-200 font-semibold whitespace-nowrap">{fmtMoney(c.amount_cents)}</td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-bold uppercase tracking-wider ${STATUS_COLORS[c.status] || 'bg-white/[0.04] text-white/55 border-white/[0.08]'}`}>
                      {c.status || 'pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PartnerAccountLayout>
  );
}
